/**
 * TeamPage — "Paelode team": the people who can sign in to this admin console (Corbin,
 * 2026-10-21: "I want to invite others for the admin").
 *
 *   GET  /v1/platform/team                        the list
 *   POST /v1/platform/team/invite                 name + email → an email with a temporary password
 *   POST /v1/platform/team/:userId/resend-invite  only while they have never signed in
 *   POST /v1/platform/team/:userId/suspend        signs them out everywhere
 *   POST /v1/platform/team/:userId/reactivate
 *
 * The API decides every rule (never yourself, never the last admin, the email must be free);
 * this page words the buttons and shows the API's own plain message when it refuses.
 */

import * as React from 'react';
import { toast } from 'sonner';
import {
  ApiError,
  apiErrorText,
  useApiMutation,
  useApiOpts,
  useApiQuery,
} from '@lodex-pro/web-shared';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'src/components/ui/button';
import { SortButton } from 'src/components/ui/sortable-head';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import { ariaSort, sortRows, useUrlSort } from '../../lib/url-state';

export type TeamStatus = 'INVITED' | 'ACTIVE' | 'SUSPENDED';

export interface TeamMember {
  readonly userId: string;
  readonly fullName: string;
  readonly email: string;
  readonly status: TeamStatus;
  readonly invitedAt: string | null;
  readonly lastSignInAt: string | null;
  readonly isYou: boolean;
}

export const TEAM_QUERY_KEY = ['platform', 'team'] as const;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const STATUS_LABEL: Readonly<Record<TeamStatus, string>> = {
  INVITED: 'Invited',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
};
const STATUS_TONE = { INVITED: 'warning', ACTIVE: 'positive', SUSPENDED: 'muted' } as const;

/**
 * The API's own plain message when it refused, else the fallback. Never ApiError.message,
 * which is "API 409: /v1/platform/team/…" (OPEN-199). A resend for someone who has already set
 * a password names them.
 */
export function teamErrorText(err: unknown, fallback: string, member?: TeamMember): string {
  if (!(err instanceof ApiError) || err.code === null || err.code === 'VALIDATION_FAILED') {
    return fallback;
  }
  if (err.code === 'PLATFORM_TEAM_ALREADY_SIGNED_IN' && member !== undefined) {
    return `${member.fullName} has already set a password, so no new invite is needed. If they’re locked out, they can use "Forgot password" on the sign-in page.`;
  }
  return apiErrorText(err, fallback);
}

/** "Oct 21, 2026, 9:05 AM" in the viewer's zone, or "Never". */
export function lastSignInText(iso: string | null): string {
  if (iso === null) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

function InviteForm(): React.JSX.Element {
  const opts = useApiOpts();
  const queryClient = useQueryClient();
  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const invite = useApiMutation<
    { fullName: string; email: string },
    { member: TeamMember; inviteEmailed: boolean }
  >(opts, '/v1/platform/team/invite', 'POST', {
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
      setFullName('');
      setEmail('');
      if (res.inviteEmailed) {
        toast.success(`Invite sent to ${res.member.email}.`);
      } else {
        toast.error(
          `${res.member.fullName} was added, but the invite email did not send. Use Resend invite.`,
        );
      }
    },
    onError: (err) =>
      toast.error(teamErrorText(err, 'Could not send the invite. Nobody was added.')),
  });
  const trimmedEmail = email.trim();
  const emailBad = trimmedEmail !== '' && !EMAIL_RE.test(trimmedEmail);
  const canSend = fullName.trim() !== '' && EMAIL_RE.test(trimmedEmail) && !invite.isPending;

  return (
    <form
      onSubmit={(e): void => {
        e.preventDefault();
        if (canSend) invite.mutate({ fullName: fullName.trim(), email: trimmedEmail });
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '520px' }}
    >
      <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Invite someone</h3>
      <p style={{ ...subText, margin: 0 }}>
        They get an email with a temporary password and choose their own when they first sign in.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <FormRow label="Name" htmlFor="team-name">
          <input
            id="team-name"
            value={fullName}
            onChange={(e): void => setFullName(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="Email" htmlFor="team-email">
          <input
            id="team-email"
            type="email"
            value={email}
            onChange={(e): void => setEmail(e.target.value)}
            style={fieldStyle}
            aria-invalid={emailBad}
          />
        </FormRow>
      </div>
      {emailBad && (
        <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '12px', margin: 0 }}>
          Enter a valid email address
        </p>
      )}
      <div>
        <Button type="submit" disabled={!canSend}>
          {invite.isPending ? 'Sending…' : 'Send invite'}
        </Button>
      </div>
    </form>
  );
}

/** One row's buttons, each with its own mutation (the user id is in the path). */
function MemberActions({ member }: { member: TeamMember }): React.JSX.Element | null {
  const opts = useApiOpts();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = React.useState(false);
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
  };
  const base = `/v1/platform/team/${member.userId}`;
  const resend = useApiMutation<Record<string, never>, { inviteEmailed: boolean }>(
    opts,
    `${base}/resend-invite`,
    'POST',
    {
      onSuccess: (res) => {
        refresh();
        if (res.inviteEmailed) toast.success(`Invite sent again to ${member.email}.`);
        else toast.error('The new invite did not send. Try again.');
      },
      onError: (err) => {
        // The list was stale: they set a password since it loaded (OPEN-199).
        if (err instanceof ApiError && err.code === 'PLATFORM_TEAM_ALREADY_SIGNED_IN') refresh();
        toast.error(teamErrorText(err, 'Could not resend the invite.', member));
      },
    },
  );
  const suspend = useApiMutation<Record<string, never>, unknown>(opts, `${base}/suspend`, 'POST', {
    onSuccess: () => {
      refresh();
      setConfirming(false);
      toast.success(`${member.fullName} is suspended and signed out everywhere.`);
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(teamErrorText(err, 'Could not suspend them. Nothing was changed.'));
    },
  });
  const reactivate = useApiMutation<Record<string, never>, unknown>(
    opts,
    `${base}/reactivate`,
    'POST',
    {
      onSuccess: () => {
        refresh();
        toast.success(`${member.fullName} can sign in again.`);
      },
      onError: (err) => toast.error(teamErrorText(err, 'Could not reactivate them.')),
    },
  );

  if (member.status === 'SUSPENDED') {
    return (
      <Button
        type="button"
        variant="outline"
        disabled={reactivate.isPending}
        onClick={(): void => reactivate.mutate({})}
      >
        {reactivate.isPending ? 'Reactivating…' : 'Reactivate'}
      </Button>
    );
  }
  if (confirming) {
    return (
      <div
        role="group"
        aria-label={`Suspend ${member.fullName}`}
        style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}
      >
        <p
          style={{ fontSize: '13px', margin: 0, color: 'var(--color-danger)', textAlign: 'right' }}
        >
          Suspend {member.fullName}? They&apos;ll be signed out everywhere and can&apos;t sign in
          until someone reactivates them.
        </p>
        <span style={{ display: 'inline-flex', gap: '6px' }}>
          <Button
            type="button"
            disabled={suspend.isPending}
            onClick={(): void => suspend.mutate({})}
          >
            {suspend.isPending ? 'Suspending…' : 'Suspend'}
          </Button>
          <Button type="button" variant="ghost" onClick={(): void => setConfirming(false)}>
            Cancel
          </Button>
        </span>
      </div>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: '6px' }}>
      {member.status === 'INVITED' && (
        <Button
          type="button"
          variant="outline"
          disabled={resend.isPending}
          onClick={(): void => resend.mutate({})}
        >
          {resend.isPending ? 'Sending…' : 'Resend invite'}
        </Button>
      )}
      {!member.isYou && (
        <Button type="button" variant="outline" onClick={(): void => setConfirming(true)}>
          Suspend
        </Button>
      )}
    </span>
  );
}

/** The columns the team table sorts by (OPEN-204); the API sends them by name. */
const TEAM_SORTS = ['name', 'email', 'lastSignIn'] as const;

export function TeamPage(): React.JSX.Element {
  const opts = useApiOpts();
  const { data, isLoading, isError } = useApiQuery<{ members: TeamMember[] }>(
    opts,
    '/v1/platform/team',
    { queryKey: TEAM_QUERY_KEY },
  );
  const { sort, toggle } = useUrlSort(TEAM_SORTS, { key: 'name', dir: 'asc' }, []);
  const members = sortRows(data?.members ?? [], sort, (m, k) => {
    switch (k) {
      case 'name':
        return m.fullName;
      case 'email':
        return m.email;
      case 'lastSignIn':
        return m.lastSignInAt;
    }
  });

  return (
    <div style={{ maxWidth: '920px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 4px' }}>Paelode team</h2>
      <p style={{ ...subText, margin: '0 0 20px' }}>
        The people who can sign in to this admin console.
      </p>

      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || data === undefined ? (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load the Paelode team.
        </p>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
            marginBottom: '28px',
          }}
        >
          <thead>
            <tr style={{ textAlign: 'left', ...subText }}>
              <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'name')}>
                <SortButton column="name" sort={sort} onSort={toggle}>
                  Name
                </SortButton>
              </th>
              <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'email')}>
                <SortButton column="email" sort={sort} onSort={toggle}>
                  Email
                </SortButton>
              </th>
              <th style={{ padding: '6px 8px' }}>Status</th>
              <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'lastSignIn')}>
                <SortButton column="lastSignIn" sort={sort} onSort={toggle}>
                  Last sign-in
                </SortButton>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr
                key={m.userId}
                data-testid={`team-row-${m.userId}`}
                style={{ borderTop: '1px solid var(--color-border-primary)' }}
              >
                <td style={{ padding: '8px' }}>
                  {m.fullName}
                  {m.isYou && <span style={{ ...subText, marginLeft: '6px' }}>(you)</span>}
                </td>
                <td style={{ padding: '8px' }}>{m.email}</td>
                <td style={{ padding: '8px' }}>
                  <Pill label={STATUS_LABEL[m.status]} tone={STATUS_TONE[m.status]} />
                </td>
                <td style={{ padding: '8px', ...subText }}>{lastSignInText(m.lastSignInAt)}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>
                  <MemberActions member={m} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <InviteForm />
    </div>
  );
}
